import { useState } from 'react';
import { AlertTriangle, CalendarClock, CheckCircle2, Loader2, Mail, Paperclip } from 'lucide-react';
import { api } from '@/lib/api';
import { estadoCorreo, fechaProgramada, horaPorDefecto, textoFacturaPropietario, textoPredefinido, textoProgramado } from '@/lib/correoLiquidacion';
import CuandoEnviar, { Cuando, errorCuando, isoDe } from './CuandoEnviar';

/**
 * Paso 6 del asistente: mandar al propietario la liquidación y la factura.
 *
 * En explotación la liquidación ES la factura que el CRM emite en nombre del
 * propietario, así que se adjunta un solo PDF: la factura de propietario.
 *
 * El texto viene escrito y se puede retocar. Sale de no-reply con respuesta a
 * gestion@, como el resto de correos del CRM.
 *
 * Se puede mandar ya o programarlo: entonces el PDF se genera ahora, se guarda
 * con la liquidación y lo manda el servidor a esa hora.
 */
export default function PasoEmailPropietario({
  liquidacion,
  owner,
  generarPdfLiquidacion,
  onLiquidacionActualizada,
  onAtras,
  onContinuar,
}: {
  liquidacion: any;
  owner: any;
  /** El PDF de la liquidación lo genera el navegador, igual que al descargarlo. */
  generarPdfLiquidacion: () => Promise<{ nombre: string; base64: string }>;
  onLiquidacionActualizada: (liq: any) => void;
  onAtras?: () => void;
  onContinuar: () => void;
}) {
  const esFacturaPropietario = liquidacion.result?.tipo === 'factura';
  const numeroPropietario = esFacturaPropietario && liquidacion.invoiceNum != null
    ? `${liquidacion.invoicePrefix ?? ''}${liquidacion.invoiceNum}`
    : '';
  const factura = esFacturaPropietario ? null : liquidacion.holdedInvoice || null;
  const [para, setPara] = useState<string>(owner?.email || '');
  const [asunto, setAsunto] = useState(
    esFacturaPropietario ? `Factura de ${liquidacion.periodo} · Sereno` : `Liquidación de ${liquidacion.periodo} · Sereno`,
  );
  const [texto, setTexto] = useState(() => esFacturaPropietario
    ? textoFacturaPropietario(owner, liquidacion.periodo, numeroPropietario)
    : textoPredefinido(owner, liquidacion.periodo, factura));
  const [adjuntarLiquidacion, setAdjuntarLiquidacion] = useState(true);
  const [adjuntarFactura, setAdjuntarFactura] = useState(Boolean(factura));
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [enviado, setEnviado] = useState<any>(null);
  const [programado, setProgramado] = useState<any>(null);
  const [cuando, setCuando] = useState<Cuando>({ modo: 'ahora' });
  const [cancelando, setCancelando] = useState(false);

  const anterior = [...(liquidacion.emailsEnviados || [])].reverse().find((e: any) => e.estado === 'enviado');
  const yaProgramado = liquidacion.correoProgramado || null;

  async function enviar() {
    setEnviando(true);
    setError('');
    try {
      const programadoPara = isoDe(cuando) || undefined;
      const liquidacionPdf = adjuntarLiquidacion ? await generarPdfLiquidacion() : undefined;
      const r = await api.enviarEmailLiquidacion(liquidacion.id, {
        para: para.trim(),
        asunto,
        texto,
        liquidacionPdf,
        adjuntarFactura: adjuntarFactura && Boolean(factura),
        programadoPara,
      });
      onLiquidacionActualizada(r.liquidacion);
      if (programadoPara) setProgramado(r.liquidacion.correoProgramado || { para, programadoPara });
      else setEnviado(r.liquidacion.emailsEnviados?.at(-1) || { para });
    } catch (err: any) {
      setError(err?.message || 'No se pudo enviar el correo');
    } finally {
      setEnviando(false);
    }
  }

  async function cancelarProgramado() {
    setCancelando(true);
    setError('');
    try {
      const r = await api.cancelarCorreoProgramado(liquidacion.id);
      onLiquidacionActualizada(r.liquidacion);
    } catch (err: any) {
      setError(err?.message || 'No se pudo cancelar');
    } finally {
      setCancelando(false);
    }
  }

  if (programado) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <CalendarClock className="text-blue-500" size={48} />
          <p className="text-lg font-semibold text-sereno-dark">Correo programado</p>
          <p className="text-sm text-sereno-dark/60">
            Saldrá {fechaProgramada(programado.programadoPara)} a {programado.para}. Puedes cancelarlo hasta entonces desde la ficha de la liquidación.
          </p>
          <button onClick={onContinuar} className="btn-primary mt-2">Continuar al pago →</button>
        </div>
      </div>
    );
  }

  if (enviado) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <CheckCircle2 className="text-green-500" size={48} />
          <p className="text-lg font-semibold text-sereno-dark">Correo enviado</p>
          <p className="text-sm text-sereno-dark/60">
            Enviado a {enviado.para}{enviado.adjuntos?.length ? ` con ${enviado.adjuntos.length} adjunto${enviado.adjuntos.length > 1 ? 's' : ''}` : ''}.
          </p>
          <button onClick={onContinuar} className="btn-primary mt-2">Continuar al pago →</button>
        </div>
      </div>
    );
  }

  const puedeEnviar = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(para.trim()) && asunto.trim() && texto.trim() && !errorCuando(cuando)
    && yaProgramado?.estado !== 'enviando';

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-sereno-dark">¿Enviar la liquidación al propietario?</h2>
        <p className="text-sm text-sereno-dark/60">Sale de no-reply@mail.tusereno.es; si responde, le llega a gestion@tusereno.es.</p>
      </div>

      {yaProgramado && (
        <div className={`flex items-start gap-2 text-sm rounded-lg border p-3 ${yaProgramado.estado === 'fallido' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
          <CalendarClock size={16} className="mt-0.5 shrink-0" />
          <span className="flex-1">
            {textoProgramado(yaProgramado)}{yaProgramado.estado === 'pendiente' ? `, a ${yaProgramado.para}` : ''}.
            {yaProgramado.estado === 'pendiente' && ' Si envías o programas otro, ese se cancela.'}
          </span>
          {yaProgramado.estado !== 'enviando' && (
            <button onClick={cancelarProgramado} disabled={cancelando} className="text-xs font-semibold underline shrink-0">
              {cancelando ? 'Cancelando…' : yaProgramado.estado === 'fallido' ? 'Descartar' : 'Cancelar'}
            </button>
          )}
        </div>
      )}

      {anterior && (
        <div className="flex items-start gap-2 text-sm rounded-lg border p-3 bg-amber-50 border-amber-200 text-amber-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>Ya se envió el {new Date(anterior.fecha).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })} a {anterior.para}. {estadoCorreo(anterior).texto}.</span>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="block text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide mb-1.5">Para</label>
          <input className="input" type="email" value={para} onChange={e => setPara(e.target.value)} placeholder="correo@ejemplo.com" />
          {!owner?.email && <p className="text-xs text-amber-700 mt-1">El propietario no tiene correo en su ficha.</p>}
        </div>
        <div>
          <label className="block text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide mb-1.5">Asunto</label>
          <input className="input" value={asunto} onChange={e => setAsunto(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide mb-1.5">Mensaje</label>
          <textarea className="input min-h-[14rem] font-[inherit] leading-relaxed" value={texto} onChange={e => setTexto(e.target.value)} />
        </div>
        <div className="space-y-2">
          <p className="text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide">Adjuntos</p>
          <label className="flex items-center gap-2 text-sm text-sereno-dark">
            <input type="checkbox" checked={adjuntarLiquidacion} onChange={e => setAdjuntarLiquidacion(e.target.checked)} />
            <Paperclip size={14} className="text-sereno-dark/50" />
            {esFacturaPropietario
              ? `Factura de propietario${numeroPropietario ? ` ${numeroPropietario}` : ''} (PDF)`
              : `Liquidación de ${liquidacion.periodo} (PDF)`}
          </label>
          {!esFacturaPropietario && (
          <label className={`flex items-center gap-2 text-sm ${factura ? 'text-sereno-dark' : 'text-sereno-dark/40'}`}>
            <input type="checkbox" checked={adjuntarFactura} disabled={!factura} onChange={e => setAdjuntarFactura(e.target.checked)} />
            <Paperclip size={14} className="text-sereno-dark/50" />
            {!factura
              ? 'Factura de Holded (no se ha creado en el paso anterior)'
              : factura.draft
                ? 'Borrador de la factura de Holded (PDF, sin número)'
                : `Factura ${factura.docNumber || ''} de Holded (PDF)`}
          </label>
          )}
        </div>
        <CuandoEnviar cuando={cuando} onCambiar={setCuando} porDefecto={() => horaPorDefecto()} disabled={enviando} />
      </div>

      {error && (
        <div className="flex items-start gap-2 text-sm rounded-lg border p-3 bg-red-50 border-red-200 text-red-700">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}

      <div className="flex justify-between items-center pt-2">
        {onAtras ? <button onClick={onAtras} className="btn-ghost flex items-center gap-2">← Atrás</button> : <span />}
        <div className="flex items-center gap-3">
          <button onClick={onContinuar} className="btn-ghost text-sm">No enviar</button>
          <button onClick={enviar} disabled={!puedeEnviar || enviando} className="btn-primary flex items-center gap-2">
            {enviando ? <Loader2 className="animate-spin" size={16} /> : cuando.modo === 'programar' ? <CalendarClock size={16} /> : <Mail size={16} />}
            {cuando.modo === 'programar' ? 'Programar envío' : 'Enviar email'}
          </button>
        </div>
      </div>
    </div>
  );
}
